import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { supabase } from "@/integrations/supabase/client";
import { Footer, Header, PAGE_CONTAINER } from "../../components/site";
import { getMijnAanvragen } from "../../lib/klantaanvragen.functions";
import { Button } from "@/components/ui/button";
import { accountHead } from "@/lib/klantaccount-shared";

export const Route = createFileRoute("/_authenticated/mijn-aanvragen")({
  head: () => accountHead("Mijn aanvragen", "Bekijk je eigen aanvragen, status en aangeleverde foto's."),
  component: MijnAanvragen,
});

function datum(waarde: string) {
  return new Date(waarde).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function MijnAanvragen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = Route.useRouteContext();
  const ophalen = useServerFn(getMijnAanvragen);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["mijn-aanvragen", user.id],
    queryFn: () => ophalen(),
  });

  async function uitloggen() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    void navigate({ to: "/auth", replace: true });
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className={`${PAGE_CONTAINER} py-14`}>
        <Button asChild variant="link" className="mb-4 px-0"><Link to="/account">← Mijn account</Link></Button>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-extrabold tracking-tight">Mijn aanvragen</h1>
          <button
            type="button"
            onClick={() => void uitloggen()}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary"
          >
            Uitloggen
          </button>
        </div>
        <p className="mt-3 text-muted-foreground">
          Hier staan al je offerteaanvragen met de documenten en foto's die je hebt aangeleverd.
        </p>

        {isLoading && <p className="mt-8 text-sm text-muted-foreground">Bezig met laden…</p>}
        {isError && (
          <p className="mt-8 text-sm font-medium text-destructive">
            Je aanvragen konden niet geladen worden. Probeer het later opnieuw.
          </p>
        )}

        {data && data.length === 0 && (
          <div className="mt-8 w-full max-w-4xl rounded-lg border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground">Je hebt nog geen aanvraag gedaan.</p>
            <Link
              to="/aanvraag"
              className="mt-4 inline-block rounded-lg bg-accent px-5 py-2.5 text-sm font-bold text-accent-foreground"
            >
              Gratis offertes aanvragen
            </Link>
          </div>
        )}

        <div className="mt-8 w-full max-w-4xl space-y-5">
          {data?.map((a) => (
            <article key={a.id} className="rounded-lg border border-border bg-card p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-lg font-bold">{a.type_opdracht ?? "Offerteaanvraag"}</h2>
                <span className="text-sm text-muted-foreground">{datum(a.created_at)}</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {a.straat} {a.huisnummer ?? ""}, {a.postcode} {a.plaats}
              </p>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                {a.soort_aanvraag && (
                  <div>
                    <dt className="font-bold">Soort aanvraag</dt>
                    <dd className="text-muted-foreground">{a.soort_aanvraag}</dd>
                  </div>
                )}
                {a.uitvoerdatum && (
                  <div>
                    <dt className="font-bold">Gewenste uitvoering</dt>
                    <dd className="text-muted-foreground">{a.uitvoerdatum}</dd>
                  </div>
                )}
                <div>
                  <dt className="font-bold">Status</dt>
                  <dd className="text-muted-foreground">{a.status}</dd>
                </div>
              </dl>
              {a.omschrijving && (
                <p className="mt-4 whitespace-pre-wrap text-sm text-muted-foreground">
                  {a.omschrijving}
                </p>
              )}
              {a.fotos.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-bold">Aangeleverde documenten en foto's</h3>
                  <ul className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {a.fotos.map((f, i) => (
                      <li key={f.url}>
                        <a href={f.url} target="_blank" rel="noreferrer">
                          <img
                            src={f.url}
                            alt={`Bijlage ${i + 1} van ${a.fotos.length} bij je aanvraag`}
                            className="aspect-square w-full rounded-lg border border-border object-cover"
                            loading="lazy"
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
